import type { CurrencyCode, SearchResultPriceFragment } from '#graphql-operations'
import { useLocaleObject } from '@layers/i18n/app/composables/useLocaleObject'

export function usePriceFormatter() {
  const { n: numberFormat, locale, getNumberFormat } = useI18n()
  const localeObject = useLocaleObject()

  const formatCHF = (amount: number) => {
    const isWholeNumber = amount % 1 === 0
    const isNegative = amount < 0
    const absAmount = Math.abs(amount)

    const formatted = numberFormat(absAmount, 'currency', {
      currency: 'CHF',
      minimumFractionDigits: isWholeNumber ? 0 : 2,
      maximumFractionDigits: 2,
    })

    let result = formatted
    if (isWholeNumber)
      result = result.replace(/(\d+)(?:\.\d{2})?$/, '$1.-')

    if (isNegative)
      result = result.replace(/^-?\s*CHF\s*/, 'CHF -')

    return result
  }

  // `n()` hands the storefront locale CODE to `Intl`, and some codes are
  // countries, not languages: ICU does not know `dk`, falls back to English and
  // prints `DKK 179` instead of `179 kr.`. Such codes format through the locale's
  // `language` (`da-DK`); codes ICU knows keep going through `n()` unchanged.
  // The locale is part of the memo key because the output depends on it.
  const lazyFormatter = useMemoize((amount: number, currencyCode: CurrencyCode, localeCode: string) => {
    if (currencyCode === 'CHF') {
      return formatCHF(amount / 100)
    }

    if (!Intl.NumberFormat.supportedLocalesOf(localeCode).length && localeObject.value?.language) {
      return new Intl.NumberFormat(localeObject.value.language, {
        ...getNumberFormat(localeCode).currency,
        currency: currencyCode,
      }).format(amount / 100)
    }

    return numberFormat(amount / 100, 'currency', { currency: currencyCode })
  })
  const format = (amount: number, currencyCode: CurrencyCode) => lazyFormatter(amount, currencyCode, locale.value)

  return (price: number | SearchResultPriceFragment | number = 0, currencyCode: CurrencyCode) => {
    if (typeof price === 'number')
      return format(price, currencyCode)

    if ('value' in price)
      return format(price.value, currencyCode)

    if ('min' in price && 'max' in price)
      return price.min === price.max ? format(price.min, currencyCode) : `${format(price.min, currencyCode)} - ${format(price.max, currencyCode)}`

    console.warn('Invalid price', price)
    return format(0, currencyCode)
  }
}
